/**
 * Draggable Section List Component
 * Reuses drag-and-drop pattern from StageGateKanban for section reordering
 */

import React, { useState } from 'react';
import { Card, Button, Badge } from '../UI';
import { GripVertical, Edit, Trash2, Brain, Type, FileText } from 'lucide-react';
import { type ReportSection } from '../../api/reports';

interface DraggableSectionListProps {
  sections: ReportSection[];
  reportId: string;
  onReorder: (newOrder: Array<{ sectionId: string; displayOrder: number }>) => void;
  onEdit: (sectionId: string) => void;
  onDelete: (sectionId: string) => void;
  canEdit?: boolean;
}

export function DraggableSectionList({
  sections,
  onReorder,
  onEdit,
  onDelete,
  canEdit = true,
}: DraggableSectionListProps) {
  const [draggedSection, setDraggedSection] = useState<ReportSection | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent, section: ReportSection) => {
    if (!canEdit) {
      e.preventDefault();
      return;
    }
    setDraggedSection(section);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', section.id);
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
    setDraggedSection(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    if (!canEdit || !draggedSection) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (!draggedSection || !canEdit) return;

    const draggedIndex = sections.findIndex((s) => s.id === draggedSection.id);
    if (draggedIndex === dropIndex) {
      setDraggedSection(null);
      setDragOverIndex(null);
      return;
    }

    // Create new order
    const newSections = [...sections];
    const [removed] = newSections.splice(draggedIndex, 1);
    newSections.splice(dropIndex, 0, removed);

    // Update display_order
    const newOrder = newSections.map((section, index) => ({
      sectionId: section.id,
      displayOrder: index,
    }));

    onReorder(newOrder);
    setDraggedSection(null);
    setDragOverIndex(null);
  };

  const getSectionTypeIcon = (section: ReportSection) => {
    if (section.content_mode === 'manual') {
      return Type;
    }
    return Brain;
  };

  const getSectionTypeColor = (section: ReportSection) => {
    if (section.is_custom) {
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200';
    }
    return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200';
  };

  if (sections.length === 0) {
    return (
      <Card>
        <div className="text-center py-12 text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No sections yet. Add a custom section to get started.</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      {sections.map((section, index) => {
        const Icon = getSectionTypeIcon(section);
        const isDragging = draggedSection?.id === section.id;
        const isDragOver = dragOverIndex === index;

        return (
          <div
            key={section.id}
            draggable={canEdit}
            onDragStart={(e) => handleDragStart(e, section)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            className={`${
              canEdit ? 'cursor-move' : 'cursor-default'
            } ${
              isDragging ? 'opacity-50' : ''
            } ${
              isDragOver
                ? 'ring-2 ring-blue-400 dark:ring-blue-600 bg-blue-50 dark:bg-blue-900/20'
                : ''
            } transition-all`}
          >
            <Card className="p-4 hover:shadow-md">
              <div className="flex items-start gap-3">
                {/* Drag Handle */}
                {canEdit && (
                  <div className="flex-shrink-0 pt-1">
                    <GripVertical className="h-5 w-5 text-gray-400" />
                  </div>
                )}

                {/* Section Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      {section.section_name}
                    </h4>
                    <Badge className={getSectionTypeColor(section)}>
                      {section.is_custom ? 'Custom' : 'Standard'}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      <Icon className="h-3 w-3 mr-1" />
                      {section.content_mode === 'ai' ? 'AI' : 'Manual'}
                    </Badge>
                    {section.section_type && section.section_type !== 'standard' && (
                      <Badge variant="outline" className="text-xs">
                        {section.section_type}
                      </Badge>
                    )}
                  </div>

                  {section.question && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                      {section.question}
                    </p>
                  )}

                  {section.answer && (
                    <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      <div className="line-clamp-3">
                        {section.answer.length > 200 ? section.answer.substring(0, 200) + '...' : section.answer}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                    <span>Confidence: {section.confidence}%</span>
                    <span>Citations: {section.citations?.length || 0}</span>
                    {(() => {
                      // Get word count from metadata or calculate from answer
                      const wordCount = section.section_metadata?.word_count ?? 
                        (section.answer ? section.answer.split(/\s+/).filter((w: string) => w.length > 0).length : 0);
                      return wordCount > 0 ? <span>Words: {wordCount.toLocaleString()}</span> : null;
                    })()}
                    <span>Order: {section.display_order ?? index}</span>
                  </div>
                </div>

                {/* Actions */}
                {canEdit && (
                  <div className="flex-shrink-0 flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onEdit(section.id)}
                      title="Edit section"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {section.is_custom && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDelete(section.id)}
                        title="Delete section"
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>
        );
      })}
    </div>
  );
}
